import * as React from 'react';
import { FC, useState, useCallback, useMemo, useEffect } from 'react';
import { ListGroupItem, ListGroup, Badge, Modal, ModalHeader, ModalBody, ModalFooter, Alert } from 'reactstrap';
import styled from 'styled-components';
import { ReportTemplateData, CatalogTemplateData, ReportSettings } from '../../../models';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { useGeneralSettings } from '../../../store/settings/hooks';
import { serviceName } from '../../../shared/appointmint/appointment';
import AssignmentIcon from '@material-ui/icons/Assignment';
import EditIcon from '@material-ui/icons/Edit';
import SparkleAIIcon from '../../../components/Icons/SparkleAIIcon';
import ReportConfigurationModal from './ReportConfigurationModal';
import ReportTemplateCatalogModal from './ReportTemplateCatalogModal';
import { Guid } from '../../../shared/utils';
import { getIconComponent } from '../../../shared/documentIconPicker';
import { useDispatch } from 'react-redux';
import { alertError, alertSuccess } from '../../../store/alerts/actions';
import { loadReportTemplates } from '../../../store/settings/actions';
import { getErrorMessage } from '../../../ajax/errors';
import { useReportTemplates } from '../../../hooks/useReportTemplates';
import {
    createReportTemplateAsync,
    updateReportTemplateAsync,
    deleteReportTemplateAsync,
} from '../../../ajax/requests';
import AppButton from '../../../components/AppButton';

interface CreateButtonProps {
    $ai?: boolean;
    id?: string;
    onClick?: () => void;
    children?: React.ReactNode;
}

const CategoryBadge = styled.span`
    display: inline-block;
    font-size: 0.7rem;
    padding: 1px var(--spacer-XS);
    border-radius: 3px;
    background: var(--bg-light);
    border: var(--border-thin) solid var(--appLigthGrey);
    color: var(--text-muted);
    white-space: nowrap;
`;

const CATEGORY_KEY_TO_TK: Record<string, TK> = {
    general: TK.reportTemplateCategoryGeneral,
    psychology: TK.reportTemplateCategoryPsychology,
    psychiatry: TK.reportTemplateCategoryPsychiatry,
    physiotherapy: TK.reportTemplateCategoryPhysiotherapy,
    nutrition: TK.reportTemplateCategoryNutrition,
    dentistry: TK.reportTemplateCategoryDentistry,
};

const CreateButton = styled.button<CreateButtonProps>`
    display: inline-flex;
    align-items: center;
    gap: var(--spacer-XS);
    background: none;
    border: var(--border-thin) solid ${p => p.$ai ? 'var(--info-color)' : 'var(--appLigthGrey)'};
    border-radius: 4px;
    color: ${p => p.$ai ? 'var(--info-color)' : 'var(--text-body)'};
    cursor: pointer;
    font-size: 0.875rem;
    padding: var(--spacer-XXS) var(--spacer-S);
    font-weight: 500;
    transition: background 0.15s, border-color 0.15s, color 0.15s;

    &:hover {
        background: ${p => p.$ai ? 'rgba(102, 126, 234, 0.08)' : 'var(--bg-light)'};
        border-color: ${p => p.$ai ? 'var(--info-color)' : 'var(--text-muted)'};
    }
`;

export interface ReportsSettingsProps {
    id: string,
}

const catalogTemplateToReportSettings = (template: CatalogTemplateData): ReportSettings => ({
    id: Guid.newGuid(),
    name: template.name,
    version: 1,
    icon: template.icon,
    colorCode: template.colorCode,
    availableForAllAppointmentTypes: template.availableForAllAppointmentTypes,
    editableAfterCreationMinutes: template.editableAfterCreationMinutes,
    availableForAppointmentsManager: template.availableForAppointmentsManager,
    availableForAppointmentTypes: [],
    mandatoryForAppointmentTypes: [],
    fields: template.fields,
    creationDate: new Date().toISOString(),
    showReportInline: template.showReportInline,
    hideHeader: template.hideHeader,
    printZoom: template.printZoom,
});

const toReportSettings = (t: ReportTemplateData): ReportSettings => ({
    id: t.templateId,
    name: t.name,
    version: t.version,
    disabled: t.disabled,
    icon: t.icon,
    colorCode: t.colorCode,
    hideHeader: t.hideHeader,
    availableForAllAppointmentTypes: t.availableForAllAppointmentTypes,
    editableAfterCreationMinutes: t.editableAfterCreationMinutes,
    creationDate: t.creationDate,
    availableForAppointmentTypes: t.availableForAppointmentTypes,
    mandatoryForAppointmentTypes: t.mandatoryForAppointmentTypes,
    availableForAppointmentsManager: t.availableForAppointmentsManager,
    shareWithClient: t.shareWithClient,
    fields: t.fields,
    showReportInline: t.showReportInline,
    printZoom: t.printZoom,
});

const toTemplateData = (r: ReportSettings): Omit<ReportTemplateData, 'id' | 'templateId'> => ({
    name: r.name,
    version: r.version,
    disabled: r.disabled,
    icon: r.icon,
    colorCode: r.colorCode,
    hideHeader: r.hideHeader,
    availableForAllAppointmentTypes: r.availableForAllAppointmentTypes,
    editableAfterCreationMinutes: r.editableAfterCreationMinutes,
    creationDate: r.creationDate,
    availableForAppointmentTypes: r.availableForAppointmentTypes,
    mandatoryForAppointmentTypes: r.mandatoryForAppointmentTypes,
    availableForAppointmentsManager: r.availableForAppointmentsManager,
    shareWithClient: r.shareWithClient,
    fields: r.fields,
    showReportInline: r.showReportInline,
    printZoom: r.printZoom,
});

const generateEmptyReportSettings = (): ReportSettings => ({
    id: Guid.newGuid(),
    fields: [],
    creationDate: new Date().toISOString(),
    availableForAppointmentTypes: [],
    mandatoryForAppointmentTypes: [],
    availableForAppointmentsManager: true,
    availableForAllAppointmentTypes: true,
    editableAfterCreationMinutes: 24 * 60,
    name: '',
    version: 1,
});

const ReportsSettings: FC<ReportsSettingsProps> = ({ id }) => {
    const t = useTranslations();
    const dispatch = useDispatch();
    const generalSettings = useGeneralSettings();

    const { templates } = useReportTemplates();
    const [editingReport, setEditingReport] = useState<ReportSettings | undefined>(undefined);
    const [editingTemplateId, setEditingTemplateId] = useState<string | undefined>(undefined);
    const [editingInitialMode, setEditingInitialMode] = useState<'manual' | 'ai' | undefined>(undefined);
    const [catalogOpen, setCatalogOpen] = useState(false);
    const [deletingTemplateId, setDeletingTemplateId] = React.useState<string | undefined>(undefined);
    const [saving, setSaving] = useState(false);

    const closeDeleteModal = React.useCallback(() => setDeletingTemplateId(undefined), []);

    const onOpenCatalog = useCallback(() => {
        setCatalogOpen(true);
    }, []);

    const onCloseCatalog = useCallback(() => {
        setCatalogOpen(false);
    }, []);

    const onSelectFromCatalog = useCallback((template: CatalogTemplateData) => {
        setCatalogOpen(false);
        setEditingTemplateId(undefined);
        setEditingInitialMode('manual');
        setEditingReport(catalogTemplateToReportSettings(template));
    }, []);

    const onStartFromScratch = useCallback(() => {
        setCatalogOpen(false);
        setEditingTemplateId(undefined);
        setEditingInitialMode('manual');
        setEditingReport(generateEmptyReportSettings());
    }, []);

    const onAddNew = useCallback((mode?: 'manual' | 'ai') => {
        setEditingTemplateId(undefined);
        setEditingInitialMode(mode);
        setEditingReport(generateEmptyReportSettings());
    }, []);

    const onSelect = useCallback((template: ReportTemplateData) => () => {
        setEditingTemplateId(template.templateId);
        setEditingReport({ ...toReportSettings(template), version: template.version + 1 });
    }, []);

    const onCloseReport = useCallback(() => {
        setEditingReport(undefined);
        setEditingTemplateId(undefined);
        setEditingInitialMode(undefined);
    }, []);

    const onCloneReport = useCallback((source: ReportSettings) => {
        setEditingTemplateId(undefined);
        setEditingReport({
            ...source,
            id: Guid.newGuid(),
            name: `${source.name} (${t(TK.duplicate)})`,
            version: 1,
            creationDate: new Date().toISOString(),
        });
    }, [t]);

    const onSaveReport = useCallback(async (newReport: ReportSettings) => {
        setSaving(true);
        try {
            if (editingTemplateId) {
                await updateReportTemplateAsync(editingTemplateId, {
                    ...toTemplateData(newReport),
                    id: '',
                    templateId: editingTemplateId,
                });
                dispatch(alertSuccess(t(TK.savedSuccessfully)));
            } else {
                await createReportTemplateAsync({
                    ...toTemplateData(newReport),
                    id: '',
                    templateId: '',
                });
                dispatch(alertSuccess(t(TK.entityCreatedSuccessfully)));
            }
            setEditingReport(undefined);
            setEditingTemplateId(undefined);
            dispatch(loadReportTemplates(true));
        } catch (err) {
            dispatch(alertError(getErrorMessage(err) || t(TK.somethingWentWrong)));
        } finally {
            setSaving(false);
        }
    }, [editingTemplateId, dispatch, t]);

    const onConfirmDelete = useCallback(async () => {
        if (!deletingTemplateId) return;
        setSaving(true);
        try {
            await deleteReportTemplateAsync(deletingTemplateId);
            dispatch(alertSuccess(t(TK.entityDeletedSuccessfully)));
            setDeletingTemplateId(undefined);
            dispatch(loadReportTemplates(true));
        } catch (err) {
            dispatch(alertError(getErrorMessage(err) || t(TK.somethingWentWrong)));
        } finally {
            setSaving(false);
        }
    }, [deletingTemplateId, dispatch, t]);

    const reportsOrder = useMemo<Record<string, number>>(
        () => !templates?.length ? {} : Object.fromEntries(
            templates.map(p => [p.templateId, templates.filter(pp => pp.name.toLocaleLowerCase() < p.name.toLocaleLowerCase()).length])
        ),
        [templates]
    );

    const sortedTemplates = useMemo(
        () => [...templates].sort((a, b) => (reportsOrder[a.templateId] ?? 0) - (reportsOrder[b.templateId] ?? 0)),
        [templates, reportsOrder]
    );

    useEffect(() => {
        dispatch(loadReportTemplates(true));
    }, [dispatch]);

    return (
        <>
            <Alert color="secondary" isOpen>
                {t(TK.reportsSettingsDescription)}
            </Alert>

            <ListGroup>
                {sortedTemplates.map((template) => {
                    const ReportIcon = getIconComponent(template.icon) || AssignmentIcon;
                    return (
                        <ListGroupItem key={template.templateId} style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--spacer-XS)', overflow: 'auto' }}>
                            <ReportIcon style={{ color: template.colorCode, flexShrink: 0, marginTop: '2px' }} />
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 'var(--spacer-XXS)', minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacer-XS)', flexWrap: 'wrap' }}>
                                    <AppButton
                                        id={`${id}-open-${template.templateId}-button`}
                                        onClick={onSelect(template)}
                                        color="link"
                                        style={{ padding: 0, textAlign: 'left', justifyContent: 'flex-start' }}
                                    >
                                        {template.name} (v{template.version})
                                    </AppButton>
                                    {template.categoryKey && CATEGORY_KEY_TO_TK[template.categoryKey] && (
                                        <CategoryBadge>{t(CATEGORY_KEY_TO_TK[template.categoryKey])}</CategoryBadge>
                                    )}
                                </div>
                                {template.availableForAppointmentTypes.length > 0 && (
                                    <div style={{ display: 'flex', gap: 'var(--spacer-XS)', flexWrap: 'wrap' }}>
                                        {template.availableForAppointmentTypes.map(type => <Badge key={type}>{serviceName(type, generalSettings)}</Badge>)}
                                    </div>
                                )}
                            </div>
                        </ListGroupItem>
                    );
                })}
                <ListGroupItem style={{ display: 'flex', gap: 'var(--spacer-S)', flexWrap: 'wrap' }}>
                    <CreateButton id={`${id}-add-button`} onClick={onOpenCatalog}>
                        <AssignmentIcon style={{ fontSize: '1rem' }} />
                        {t(TK.chooseFromCatalog)}
                    </CreateButton>
                    <CreateButton id={`${id}-add-manual-button`} onClick={() => onAddNew('manual')}>
                        <EditIcon style={{ fontSize: '1rem' }} />
                        {t(TK.createManually)}
                    </CreateButton>
                    <CreateButton id={`${id}-add-ai-button`} $ai onClick={() => onAddNew('ai')}>
                        <SparkleAIIcon style={{ fontSize: '1rem' }} />
                        {t(TK.createWithAI)}
                    </CreateButton>
                </ListGroupItem>
            </ListGroup>

            <ReportTemplateCatalogModal
                isOpen={catalogOpen}
                onSelectTemplate={onSelectFromCatalog}
                onStartFromScratch={onStartFromScratch}
                onClose={onCloseCatalog}
            />

            <ReportConfigurationModal
                defaultValue={editingReport}
                isNew={!editingTemplateId}
                initialMode={editingInitialMode}
                onSave={onSaveReport}
                onClose={onCloseReport}
                onDelete={setDeletingTemplateId}
                onClone={onCloneReport}
            />

            <Modal isOpen={!!deletingTemplateId} toggle={closeDeleteModal}>
                <ModalHeader toggle={closeDeleteModal}>{t(TK.areYouSure)}</ModalHeader>
                <ModalBody>{t(TK.youRAboutToDelete)}</ModalBody>
                <ModalFooter>
                    <AppButton
                        id={`${id}-confirm-delete-button`}
                        color="danger"
                        disabled={saving}
                        onClick={onConfirmDelete}
                    >
                        {t(TK.delete)}
                    </AppButton>{' '}
                    <AppButton id={`${id}-cancel-delete-button`} color="secondary" onClick={closeDeleteModal}>
                        {t(TK.cancel)}
                    </AppButton>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default ReportsSettings;
